import { useTheme, themeColors } from '../ThemeContext';
import { booths, Booth } from '../../../mocks/boothData';
import { useTranslation } from 'react-i18next';

interface AIRecommendationScreenProps {
  boothStates: Record<string, Booth['status']>;
  onBoothSelect: (booth: Booth) => void;
}

const sponsorLabel = (level: string, t: (k: string) => string) => {
  if (level === 'platinum') return t('sponsor.platinum');
  if (level === 'gold') return t('sponsor.gold');
  if (level === 'standard') return t('sponsor.standard');
  return t('sponsor.none');
};

const AIRecommendationScreen = ({ boothStates, onBoothSelect }: AIRecommendationScreenProps) => {
  const { theme } = useTheme();
  const c = themeColors[theme];
  const isDark = theme === 'dark';
  const { t } = useTranslation();

  const sortedBooths = [...booths].map(b => ({ ...b, status: boothStates[b.id] || b.status })).sort((a, b) => b.aiScore - a.aiScore);
  const mustVisit = sortedBooths.filter(b => b.aiScore >= 90 && b.status !== 'visited');
  const nearby = sortedBooths.filter(b => b.aiScore >= 75 && b.aiScore < 90 && b.status !== 'visited');
  const notVisited = sortedBooths.filter(b => b.aiScore < 75 && b.status !== 'visited');

  const sections = [
    { key: 'high' as const, items: mustVisit, label: t('ai.mustVisit'), icon: 'ri-fire-line', colorDark: 'text-rose-400', bgDark: 'bg-rose-500/10', borderDark: 'border-rose-500/30', colorLight: 'text-rose-600', bgLight: 'bg-rose-50', borderLight: 'border-rose-200', scoreColor: '#f87171', scoreColorLight: '#dc2626' },
    { key: 'medium' as const, items: nearby, label: t('ai.nearby'), icon: 'ri-map-pin-line', colorDark: 'text-amber-400', bgDark: 'bg-amber-500/10', borderDark: 'border-amber-500/30', colorLight: 'text-amber-600', bgLight: 'bg-amber-50', borderLight: 'border-amber-200', scoreColor: '#fbbf24', scoreColorLight: '#d97706' },
    { key: 'low' as const, items: notVisited, label: t('ai.notVisited'), icon: 'ri-footprint-line', colorDark: 'text-sky-400', bgDark: 'bg-sky-500/10', borderDark: 'border-sky-500/30', colorLight: 'text-sky-600', bgLight: 'bg-sky-50', borderLight: 'border-sky-200', scoreColor: '#38bdf8', scoreColorLight: '#0284c7' },
  ];

  return (
    <div className={`flex flex-col min-h-screen pb-24 ${c.bg}`}>
      <div className={`sticky top-0 z-20 ${c.navBg} backdrop-blur-xl border-b ${c.navBorder} px-5 pt-12 pb-4`}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h1 className={`text-xl font-black ${c.text}`}>{t('ai.title')}</h1>
            <p className={`text-xs ${c.textMuted}`}>{t('ai.subtitle')}</p>
          </div>
          <div className={`w-9 h-9 flex items-center justify-center rounded-xl ${isDark ? 'bg-[#a78bfa]/20 text-[#a78bfa]' : 'bg-purple-100 text-purple-600'} relative`}>
            <i className="ri-robot-2-line text-lg"></i>
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-emerald-400 rounded-full border-2 border-transparent animate-pulse"></span>
          </div>
        </div>
        <div className={`flex items-center gap-2 p-3 rounded-xl mb-3 ${isDark ? 'bg-[#111827] border border-[#1e2d47]' : 'bg-white border border-gray-200'}`}>
          <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${isDark ? 'bg-[#a78bfa]/20' : 'bg-purple-100'}`}>
            <i className={`ri-sparkling-2-line ${isDark ? 'text-[#a78bfa]' : 'text-purple-600'}`}></i>
          </div>
          <div className="flex-1">
            <p className={`text-xs font-bold ${c.text}`}>{t('ai.active')}</p>
            <p className={`text-[10px] ${c.textMuted}`}>{t('ai.analyzing')}</p>
          </div>
          <div className="flex gap-1">
            {[1, 2, 3].map(i => (
              <div key={i} className={`w-1.5 rounded-full animate-pulse ${isDark ? 'bg-[#a78bfa]' : 'bg-purple-500'}`} style={{ animationDelay: `${i * 0.15}s`, height: `${8 + i * 4}px` }} />
            ))}
          </div>
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {['AI & Automation', 'Robotics', 'FinTech', 'Cloud'].map(tag => (
            <span key={tag} className={`px-3 py-1 rounded-full text-[10px] font-semibold whitespace-nowrap border ${isDark ? 'bg-[#00d4ff]/10 text-[#00d4ff] border-[#00d4ff]/20' : 'bg-blue-50 text-blue-700 border-blue-100'}`}>{tag}</span>
          ))}
        </div>
      </div>

      <div className="px-5 pt-4 grid grid-cols-3 gap-3 mb-5">
        {[
          { icon: 'ri-fire-line', val: mustVisit.length, label: t('ai.stat.mustVisit'), colorDark: 'text-rose-400 bg-rose-500/10', colorLight: 'text-rose-600 bg-rose-50' },
          { icon: 'ri-map-pin-line', val: nearby.length, label: t('ai.stat.nearby'), colorDark: 'text-amber-400 bg-amber-500/10', colorLight: 'text-amber-600 bg-amber-50' },
          { icon: 'ri-footprint-line', val: notVisited.length, label: t('ai.stat.unexplored'), colorDark: 'text-sky-400 bg-sky-500/10', colorLight: 'text-sky-600 bg-sky-50' },
        ].map(stat => {
          const [tc, bgc] = (isDark ? stat.colorDark : stat.colorLight).split(' ');
          return (
            <div key={stat.label} className={`flex flex-col items-center gap-1 p-3 rounded-xl border ${c.bgCard} ${c.border}`}>
              <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${bgc}`}><i className={`${stat.icon} ${tc}`}></i></div>
              <span className={`text-xl font-black ${c.text}`}>{stat.val}</span>
              <span className={`text-[10px] ${c.textMuted}`}>{stat.label}</span>
            </div>
          );
        })}
      </div>

      <div className="px-5 flex flex-col gap-6">
        {sections.map(({ key, items, label, icon, colorDark, bgDark, borderDark, colorLight, bgLight, borderLight, scoreColor, scoreColorLight }) => {
          if (items.length === 0) return null;
          const tc = isDark ? colorDark : colorLight;
          const bg = isDark ? bgDark : bgLight;
          const border = isDark ? borderDark : borderLight;
          const sc = isDark ? scoreColor : scoreColorLight;
          return (
            <div key={key}>
              <div className="flex items-center gap-2 mb-3">
                <div className={`w-7 h-7 flex items-center justify-center rounded-lg border ${bg} ${border}`}><i className={`${icon} text-sm ${tc}`}></i></div>
                <span className={`text-sm font-bold ${c.text}`}>{label}</span>
                <span className={`ml-auto text-xs font-semibold px-2 py-0.5 rounded-full ${bg} ${tc}`}>{items.length}</span>
              </div>
              <div className="flex flex-col gap-3">
                {items.map(booth => {
                  const isSponsor = booth.sponsorLevel !== 'none';
                  return (
                    <button key={booth.id} onClick={() => onBoothSelect(booth)} className={`w-full text-left p-4 rounded-2xl border transition-all duration-200 active:scale-[0.98] cursor-pointer ${c.bgCard} ${c.border}`}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            {booth.sponsorLevel !== 'none' && booth.sponsorLevel !== 'standard' && (
                              <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${booth.sponsorLevel === 'platinum' ? 'bg-gradient-to-r from-slate-300 to-white text-slate-800' : 'bg-gradient-to-r from-amber-400 to-yellow-300 text-amber-900'}`}>{sponsorLabel(booth.sponsorLevel, t)}</span>
                            )}
                            <span className={`text-[9px] font-semibold px-2 py-0.5 rounded-full ${isDark ? 'bg-[#1e2d47] text-gray-400' : 'bg-gray-100 text-gray-500'}`}>{booth.boothNumber}</span>
                          </div>
                          <h3 className={`text-sm font-bold ${c.text} ${!isSponsor ? 'blur-[1px]' : ''}`}>{booth.company}</h3>
                          <p className={`text-[11px] ${c.textMuted}`}>{booth.industry} • {booth.hall}</p>
                        </div>
                        <div className="flex flex-col items-center">
                          <div className="w-12 h-12 rounded-full flex items-center justify-center font-black text-sm" style={{ background: `conic-gradient(${sc} ${booth.aiScore}%, transparent 0)`, border: `2px solid ${isDark ? '#1e2d47' : '#e5e7eb'}` }}>
                            <div className={`w-9 h-9 rounded-full flex items-center justify-center ${c.bgCard}`}>
                              <span className={`text-xs font-black ${tc}`}>{booth.aiScore}</span>
                            </div>
                          </div>
                          <span className={`text-[9px] ${c.textMuted} mt-0.5`}>{t('ai.score')}</span>
                        </div>
                      </div>
                      <div className={`flex items-start gap-2 p-2.5 rounded-xl ${isDark ? 'bg-[#0D1117]' : 'bg-gray-50'} border ${c.border}`}>
                        <i className={`ri-robot-line text-xs mt-0.5 ${isDark ? 'text-[#a78bfa]' : 'text-purple-500'} flex-shrink-0`}></i>
                        <p className={`text-[10px] ${c.textMuted} leading-relaxed`}>{isSponsor ? booth.aiReason : t('booth.limitedAI')}</p>
                      </div>
                      <div className="flex gap-1.5 flex-wrap mt-2">
                        {booth.tags.map(tg => (<span key={tg} className={`text-[10px] px-2 py-0.5 rounded-full ${isDark ? 'bg-[#1e2d47] text-gray-400' : 'bg-gray-100 text-gray-500'}`}>{tg}</span>))}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
        {mustVisit.length + nearby.length + notVisited.length === 0 && (
          <div className={`text-center py-16 ${c.textMuted}`}>
            <i className="ri-checkbox-circle-line text-4xl mb-3 block text-emerald-400"></i>
            <p className="text-sm font-bold">{t('ai.allDone')}</p>
            <p className="text-xs mt-1">{t('ai.allDoneDesc')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIRecommendationScreen;
